import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const KEY = "fitsme.cookie_consent_v1";

/**
 * GDPR-friendly cookie banner.
 * - Persists choice locally so we don't ask again.
 * - If the user is signed in, ALSO writes the consent server-side via /api/account/consents.
 * - Dismissible via close (X) button OR the Escape key (implicit "essential only").
 */
export const CookieBanner = () => {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem(KEY)) {
            // Slight delay so it doesn't fight the LCP
            const t = setTimeout(() => setVisible(true), 1200);
            return () => clearTimeout(t);
        }
    }, []);

    const record = async (granted) => {
        localStorage.setItem(KEY, JSON.stringify({ analytics: granted, at: new Date().toISOString() }));
        setVisible(false);
        if (user) {
            try {
                await api.post("/account/consents", { consent_type: "cookies", granted });
                await api.post("/account/consents", { consent_type: "analytics", granted });
            } catch { /* non-fatal */ }
        }
    };

    // Escape closes with the "essential only" (analytics=false) choice
    useEffect(() => {
        if (!visible) return;
        const onKey = (e) => { if (e.key === "Escape") record(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!visible) return null;

    return (
        <div
            data-testid="cookie-banner"
            className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 z-[55] max-w-md"
        >
            <div className="relative border-4 border-black bg-white shadow-[8px_8px_0_#CCFF00] p-4">
                <button
                    onClick={() => record(false)}
                    data-testid="cookie-close"
                    aria-label="Dismiss — essential cookies only"
                    className="absolute top-2 right-2 border-2 border-black bg-white p-1 hover:bg-black hover:text-[#CCFF00] transition-colors"
                >
                    <X className="w-3 h-3" />
                </button>
                <p className="font-mono text-[10px] uppercase tracking-widest text-black/60">/ Cookies & analytics /</p>
                <p className="font-mono text-xs mt-2 leading-relaxed">
                    We use cookies to keep you signed in. Analytics cookies help us improve fit recommendations — they're optional.
                    See our <a href="/privacy" className="underline">Privacy Policy</a>.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                    <button
                        onClick={() => record(true)}
                        data-testid="cookie-accept"
                        className="bg-black text-[#CCFF00] border-2 border-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm"
                    >Accept all</button>
                    <button
                        onClick={() => record(false)}
                        data-testid="cookie-reject"
                        className="bg-white text-black border-2 border-black px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm"
                    >Essential only</button>
                </div>
            </div>
        </div>
    );
};
